/**
 * chatWsBus — one WebSocket for the whole desktop app.
 *
 * The always-on notifications socket (useGlobalNotificationsWs, mounted in the
 * sidebar for the app's lifetime) is the ONLY chat socket. Every frame it
 * receives is republished here; pages that used to open a private second
 * socket (Chat, Calls) subscribe instead via useSharedChatWs.
 *
 * Why: the server delivers every frame to every socket a user holds, so the
 * old two-socket layout meant each message, presence flip, typing frame and
 * receipt was sent, parsed and dispatched twice, both sockets heartbeated,
 * and both acked delivery. One socket halves that work on both ends and
 * removes a whole class of "which socket is stale" reconnect races.
 *
 * No replay: a page that mounts while the socket is already open loads its own
 * data from REST on mount, so it needs nothing back-dated. Replaying a
 * remembered `connected` would re-run the reconnect catch-up (a second
 * conversation list, a second presence hydrate, a `delivered_sync`) on every
 * navigation into the page, and replaying a remembered `presence_snapshot`
 * would be worse than useless: it is only refreshed once per handshake, and
 * the chat page feeds a snapshot into `bulkHydrate`, which PRUNES every user
 * the snapshot omits. An hours-old snapshot would mark everyone who came
 * online since as offline and delete everyone who joined the branch since.
 * Genuine reconnects still publish a real `connected` to live subscribers.
 */

type Handler = (evt: any) => void;

const handlers = new Set<Handler>();
let sendImpl: ((data: object) => boolean) | null = null;

/** Fan a received frame out to every subscriber (each isolated from the others). */
export function publishChatWsEvent(evt: any): void {
  for (const handler of Array.from(handlers)) {
    try {
      handler(evt);
    } catch {
      /* one page's handler must not break delivery to the others */
    }
  }
}

/** Registered by the socket owner; `null` while there is no live socket. */
export function setChatWsSend(fn: ((data: object) => boolean) | null): void {
  sendImpl = fn;
}

/** Write a frame on the shared socket. False when nothing is connected. */
export function chatWsSend(data: object): boolean {
  if (!sendImpl) return false;
  try {
    return sendImpl(data);
  } catch {
    return false;
  }
}

export function subscribeChatWs(handler: Handler): () => void {
  handlers.add(handler);
  return () => {
    handlers.delete(handler);
  };
}

export function chatWsSubscriberCount(): number {
  return handlers.size;
}

/**
 * Test hook — forget every subscriber and the sender.
 *
 * Deliberately NOT called from app code on logout: the bus holds no
 * account-scoped state (that is the point of having no replay), each page
 * removes its own handler when it unmounts, and the socket owner nulls the
 * sender in its cleanup. A production-side `handlers.clear()` would also fire
 * on React StrictMode's dev remount and orphan pages that had already
 * subscribed.
 */
export function __resetChatWsBusForTests(): void {
  handlers.clear();
  sendImpl = null;
}
